import { useState, useEffect } from "react";
import {
  Invoice,
  InvoiceStatusEnum,
  InvoiceLineInput,
} from "@forgepoint/shared";
import { CompanySelector } from "@/components/CompanySelector";
import { InvoiceLineItemsEditor } from "./InvoiceLineItemsEditor";
import { Badge } from "@/components/ui/badge";
import { cn, formatCurrency } from "@/lib/utils";
import Decimal from "decimal.js";

interface InvoiceEditorProps {
  invoice?: Invoice | null;
  readOnly?: boolean;
  showHeader?: boolean;
  onChange?: (data: {
    salesOrderId?: string;
    customerCompanyId?: string;
    lines: InvoiceLineInput[];
  }) => void;
}

export function InvoiceEditor({
  invoice,
  readOnly = false,
  showHeader = true,
  onChange,
}: InvoiceEditorProps) {
  const [formData, setFormData] = useState<{
    salesOrderId: string | undefined;
    customerCompanyId: string | undefined;
    lines: InvoiceLineInput[];
  }>({
    salesOrderId: invoice?.salesOrderId || undefined,
    customerCompanyId: invoice?.customerCompanyId || undefined,
    lines: (invoice?.lines || []).map((line) => ({
      itemId: line.itemId,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    })),
  });

  useEffect(() => {
    setFormData({
      salesOrderId: invoice?.salesOrderId || undefined,
      customerCompanyId: invoice?.customerCompanyId || undefined,
      lines: (invoice?.lines || []).map((line) => ({
        itemId: line.itemId,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
    });
  }, [invoice]);

  const updateFormData = (
    updates: Partial<{
      salesOrderId: string | undefined;
      customerCompanyId: string | undefined;
      lines: InvoiceLineInput[];
    }>,
  ) => {
    const newFormData = { ...formData, ...updates };
    setFormData(newFormData);
    onChange?.(newFormData);
  };

  const calculateTotal = (): string => {
    const total = formData.lines.reduce((sum, line) => {
      try {
        const quantity = new Decimal(line.quantity || 0);
        const unitPrice = new Decimal(line.unitPrice || 0);
        return sum.add(quantity.mul(unitPrice));
      } catch {
        return sum;
      }
    }, new Decimal(0));
    return total.toFixed(2);
  };

  const isFinalized = invoice?.status === InvoiceStatusEnum.FINALIZED;

  return (
    <div className="flex flex-col h-full">
      {showHeader && (
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold">
                {invoice
                  ? `Invoice ${invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ""}`
                  : "New Invoice"}
              </h2>
              {invoice?.status && (
                <Badge
                  variant={
                    invoice.status === InvoiceStatusEnum.FINALIZED
                      ? "default"
                      : "secondary"
                  }
                  className={cn(
                    invoice.status === InvoiceStatusEnum.FINALIZED &&
                      "bg-green-600",
                  )}
                >
                  {invoice.status.toLowerCase()}
                </Badge>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold">
                {formatCurrency(calculateTotal())}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-6">
          {invoice?.createdAt && (
            <div className="grid gap-2">
              <label className="text-sm font-medium text-muted-foreground">
                Created Date
              </label>
              <p className="text-sm">
                {new Date(invoice.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          )}

          <div className="grid gap-2">
            <label className="text-sm font-medium text-muted-foreground">
              Customer Company
            </label>
            <CompanySelector
              currentlySelectedId={formData.customerCompanyId || null}
              onSelectId={(id) =>
                updateFormData({ customerCompanyId: id || undefined })
              }
              placeholder="Select a customer company (optional)"
              disabled={readOnly || isFinalized}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium text-muted-foreground">
              Line Items
            </label>
            <InvoiceLineItemsEditor
              lines={formData.lines || []}
              onChange={(lines) => updateFormData({ lines })}
              disabled={readOnly || isFinalized}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
